import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Profile, Session } from '@ficpod/core-types'
import {
  sharedAuthLogin,
  sharedAuthSignup,
  sharedAuthLogout,
  sharedAuthGetSession,
  sharedAuthOAuth,
  handleAuthCallback,
  getProfile,
  createSupabaseClient,
  getSupabase,
} from './index'

export interface AuthState {
  user: Profile | null
  session: Session | null
  loading: boolean
  initialized: boolean
}

export function createAuthStore(storeId: string = 'auth') {
  return defineStore(storeId, () => {
    const user = ref<Profile | null>(null)
    const session = ref<Session | null>(null)
    const loading = ref(true)
    const initialized = ref(false)

    const isAuthenticated = computed(() => !!user.value && !!session.value)
    const isAdmin = computed(() => user.value?.is_admin ?? false)

    async function initialize(supabaseUrl: string, supabaseKey: string) {
      if (initialized.value) return

      createSupabaseClient(supabaseUrl, supabaseKey)

      try {
        const result = await sharedAuthGetSession()
        
        if (result.authenticated && result.user && result.access_token) {
          session.value = {
            access_token: result.access_token,
            refresh_token: result.refresh_token || '',
            expires_in: 3600,
            expires_at: Date.now() / 1000 + 3600,
            user: result.user
          }
          
          const profile = await getProfile(result.user.id).catch(() => null)
          user.value = profile
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
      } finally {
        loading.value = false
        initialized.value = true
      }
    }

    async function signIn(email: string, password: string) {
      loading.value = true
      try {
        const result = await sharedAuthLogin(email, password)
        
        if (result.success && result.user && result.access_token) {
          session.value = {
            access_token: result.access_token,
            refresh_token: result.refresh_token || '',
            expires_in: 3600,
            expires_at: Date.now() / 1000 + 3600,
            user: result.user
          }
          
          const profile = await getProfile(result.user.id).catch(() => null)
          user.value = profile
          return { success: true }
        }
        
        return { success: false, error: result.error || 'Sign in failed' }
      } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Sign in failed' }
      } finally {
        loading.value = false
      }
    }

    async function signUp(email: string, password: string, username: string, avatarUrl?: string) {
      loading.value = true
      try {
        const result = await sharedAuthSignup(email, password, username, avatarUrl)
        
        if (result.success && result.user && result.access_token) {
          session.value = {
            access_token: result.access_token,
            refresh_token: result.refresh_token || '',
            expires_in: 3600,
            expires_at: Date.now() / 1000 + 3600,
            user: result.user
          }
          
          const profile = await getProfile(result.user.id).catch(() => null)
          user.value = profile
          return { success: true, needsConfirmation: false }
        }
        
        return { 
          success: true, 
          needsConfirmation: result.needsConfirmation || false 
        }
      } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Sign up failed' }
      } finally {
        loading.value = false
      }
    }

    async function signOut() {
      loading.value = true
      try {
        await sharedAuthLogout()
        user.value = null
        session.value = null
        return { success: true }
      } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Sign out failed' }
      } finally {
        loading.value = false
      }
    }

    async function refreshUser() {
      try {
        const supabase = getSupabase()
        const { data: { user: supaUser } } = await supabase.auth.getUser()
        
        if (supaUser) {
          if (!session.value) {
            const { data: { session: newSession } } = await supabase.auth.getSession()
            if (newSession) {
              session.value = {
                access_token: newSession.access_token,
                refresh_token: newSession.refresh_token || '',
                expires_in: newSession.expires_in,
                expires_at: newSession.expires_at || Date.now() / 1000 + 3600,
                user: { id: supaUser.id, email: supaUser.email || '' }
              }
            }
          }
          
          const profile = await getProfile(supaUser.id).catch(() => null)
          user.value = profile
        }
      } catch (error) {
        console.error('Failed to refresh user:', error)
      }
    }

    async function signInWithOAuth(provider: 'google' | 'github', redirectTo?: string) {
      const result = await sharedAuthOAuth(provider, redirectTo || window.location.origin)
      
      if (result.success && result.url) {
        return { success: true, url: result.url }
      }
      
      return { success: false, error: result.error || 'Failed to initiate OAuth' }
    }

    async function handleCallback(sessionId: string, token: string, refreshToken: string) {
      await handleAuthCallback(sessionId, token, refreshToken)
      
      const supabase = getSupabase()
      const { data: { user: supaUser } } = await supabase.auth.getUser()
      
      if (supaUser) {
        session.value = {
          access_token: token,
          refresh_token: refreshToken,
          expires_in: 3600,
          expires_at: Date.now() / 1000 + 3600,
          user: {
            id: supaUser.id,
            email: supaUser.email || ''
          }
        }
        
        const profile = await getProfile(supaUser.id).catch(() => null)
        user.value = profile
      }
    }

    function onAuthChange(callback: (user: Profile | null) => void) {
      if (typeof window === 'undefined') return () => {}
      
      const client = getSupabase()
      if (!client) return () => {}
      
      const { data: { subscription } } = client.auth.onAuthStateChange(async (event, supaSession) => {
        if (event === 'SIGNED_IN' && supaSession) {
          const profile = await getProfile(supaSession.user.id).catch(() => null)
          callback(profile)
        } else if (event === 'SIGNED_OUT') {
          callback(null)
        }
      })
      
      return () => subscription.unsubscribe()
    }

    return {
      user,
      session,
      loading,
      initialized,
      isAuthenticated,
      isAdmin,
      initialize,
      signIn,
      signUp,
      signOut,
      refreshUser,
      signInWithOAuth,
      handleCallback,
      onAuthChange
    }
  })
}

export const useSharedAuthStore = createAuthStore('shared-auth')
